import { useState, Fragment, useEffect } from 'react';

import { getTutorials } from '../../../../store/tutorials';
import { useAppSelector } from '../../../../store/configureStore';
import { tutorialConstants } from '../../../../utils/constants';
import { MTInspectionReportData, PAUTCoverData, PAUTInspectionItem, PAUTItem, PAUTOverview, PAUTSummaryData, PAUTWeldScanOverview, PDFImageProps, VTEquipmentAnomalyData } from '../../../../utils/models';

import Button from '../../../themed/button';
import Modal from '../../../themed/modal';
import Elevation from '../../../themed/instruction-modals/elevation';
import ETAnomalySummary from '../et/etAnomalySummary';
import PAUTCoverForm from '../../../forms/paut/pautCoverForm';
import PAUTIsoPageForm from '../../../forms/paut/pautIsoPageForm';
import PAUTOverviewForm from '../../../forms/paut/pautOverviewForm';
import PAUTInspectionForm from '../../../forms/paut/pautInspectionItemForm';
import PAUTItemForm from '../../../forms/paut/pautItemForm';
import PAUTMTIForm from '../../../forms/paut/pautMtiForm';
import PAUTPhotoRecordForm from '../../../forms/paut/pautPhotoRecordForm';
import PAUTWeldScanForm from '../../../forms/paut/pautWeldScanForm';
import PAUTSummaryForm from '../../../forms/paut/pautSummaryForm';
import PAUTReport from './';
import Zippy from '../../../themed/zippy';

import useAutoSaveReport from '../../../../hooks/useAutoSave';
import useProjectDetails from '../../../../hooks/useProjectDetails';

const CreatePAUTReport = () => {
	const [pautCoverData, setPAUTCoverData] = useState<PAUTCoverData | null>();
	const [summaryData, setSummaryData] = useState<PAUTSummaryData | null>();
	const [mtiData, setMTIData] = useState<MTInspectionReportData | null>();
	const [isoImages, setIsoImages] = useState<PDFImageProps[]>([]);
	const [photoData, setPhotoData] = useState<PDFImageProps[]>([]);
	const [anomalyData, setAnomalyData] = useState<VTEquipmentAnomalyData[]>([]);
	const [shearWaveData, setShearWaveData] = useState<PAUTWeldScanOverview>();
	const [pautOverview, setPAUTOverview] = useState<PAUTOverview>();
	const [pautInspection, setPAUTInspection] = useState<PAUTInspectionItem>();
	const [pautItem, setPAUTItem] = useState<PAUTItem>();
	const [previewReport, setPreviewReport] = useState<boolean>(false);

	const togglePreviewReport = (): void => {
		setPreviewReport((p: boolean) => !p);
	};

	const getReportDetails = () => {
		return {
			summaryData,
			coverSheetData: pautCoverData,
			mtiReportData: mtiData,
			isoImages,
			anomalyData,
			pautInspectionItem: pautInspection,
			pautItem,
			photoData,
			pautOverview,
			weldScanOverview: shearWaveData,
			projectDetails,
		};
	};

	const handleSaveAndPreview = () => {
		autoSave.saveReport();
		togglePreviewReport();
	};

	const { projectDetails, error } = useProjectDetails();
	const { activeTutorial } = useAppSelector(getTutorials);

	const reportDetails = getReportDetails();
	const autoSave = useAutoSaveReport(reportDetails);

	useEffect(() => {
		type ReportData = ReturnType<typeof getReportDetails>;
		const cachedReport = autoSave.oldReportData as ReportData | null;

		if (cachedReport) {
			setAnomalyData(cachedReport.anomalyData);
			setPAUTCoverData(cachedReport.coverSheetData);
			setSummaryData(cachedReport.summaryData);
			setIsoImages(cachedReport.isoImages);
			setPhotoData(cachedReport.photoData);
			setShearWaveData(cachedReport.weldScanOverview);
			setPAUTOverview(cachedReport.pautOverview);
			setPAUTItem(cachedReport.pautItem);
			setPAUTInspection(cachedReport.pautInspectionItem);
		}
	}, [autoSave.oldReportData]);

	return (
		<Fragment>
			<Modal isVisible={previewReport} onClose={togglePreviewReport}>
				<PAUTReport {...reportDetails} />
			</Modal>

			<Zippy headerTitle="Cover Sheet" isValid={!!pautCoverData}>
				<PAUTCoverForm onProceed={setPAUTCoverData} />
			</Zippy>

			<Zippy headerTitle="Summary Sheet" isValid={!!summaryData}>
				<PAUTSummaryForm onProceed={setSummaryData} />
			</Zippy>

			<Zippy
				headerTitle="Isometric Drawing Sheet"
				isValid={isoImages.length > 0}
			>
				<PAUTIsoPageForm onProceed={setIsoImages} />
			</Zippy>

			<Zippy headerTitle="PAUT Overview" isValid={!!pautOverview}>
				<PAUTOverviewForm onProceed={setPAUTOverview} />
			</Zippy>

			<Zippy headerTitle="PAUT Inspection Details" isValid={!!pautInspection}>
				<PAUTInspectionForm onProceed={setPAUTInspection} />
			</Zippy>

			<Zippy headerTitle="PAUT Item Details" isValid={!!pautItem}>
				<PAUTItemForm onProceed={setPAUTItem} />
			</Zippy>

			<Zippy
				headerTitle="Magnetic Particle Inspection Report"
				isValid={!!mtiData}
			>
				<PAUTMTIForm onProceed={setMTIData} />
			</Zippy>

			<Zippy
				headerTitle="Shearwave Inspection Report (Weld thick =or > 10mm)"
				isValid={!!shearWaveData}
			>
				<PAUTWeldScanForm onProceed={setShearWaveData} />
			</Zippy>

			<Zippy headerTitle="Visual Inspection" isValid={anomalyData.length > 0}>
				<ETAnomalySummary
					anomalyData={anomalyData}
					onDataChange={setAnomalyData}
				/>
			</Zippy>

			<Zippy headerTitle="Photographic Record" isValid={photoData.length > 0}>
				<PAUTPhotoRecordForm onProceed={setPhotoData} />
			</Zippy>

			<div className="d-flex justify-content-end">
				<Elevation
					isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}
				>
					<Button
						label="Save and Preview Report"
						disabled={error}
						onClick={handleSaveAndPreview}
					/>
				</Elevation>
			</div>
		</Fragment>
	);
};

export default CreatePAUTReport;
